import { useMemo } from 'react'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import { BaseWidget } from './BaseWidget'
import { PeriodOverrideControl } from '../controls/PeriodOverrideControl'
import { SelectControl } from '../controls/SelectControl'
import { NumberControl } from '../controls/NumberControl'
import useSharedCollection from '../../../../firebase/useSharedCollection'
import { useFirestoreQuery } from '../../../../firebase/useFirestore'
import useSettings from '../../../../hooks/Settings'
import HouseVisitsReportTableVisualization from '../../../dataVisualizations/HouseVisitsReportTableVisualization'
import {
    aggregateMeasuresByType,
} from '../../../../helpers/liveReportHouseVisits/aggregateHouseVisitReportData'
import ModulePlaceholder from './_shared/ModulePlaceholder'
import isTemplateMode from './_shared/isTemplateMode'
import useWidgetPeriod from './_shared/useWidgetPeriod'

/**
 * @extends {BaseWidget}
 */
export class HouseVisitsMeasuresWidget extends BaseWidget {
    getName() { return 'Maatregelen per type' }
    getCategory() { return 'huishoudens' }
    getRequiredModule() { return 'housevisits' }
    getIcon() { return <BuildOutlinedIcon /> }

    getControls() {
        return {
            dateRange: new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de maatregelen toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            groupBy: new SelectControl({
                label: 'Groeperen op',
                description: 'Toon losse materialen of bundel op categorie.',
                defaultValue: 'material',
                options: {
                    choices: [
                        { value: 'material', label: 'Materiaal' },
                        { value: 'category', label: 'Categorie' },
                    ],
                },
            }),
            statusFilter: new SelectControl({
                label: 'Status',
                description: 'Welke materiaalregels meetellen.',
                defaultValue: 'done',
                options: {
                    choices: [
                        { value: 'done', label: 'Gebruikt' },
                        { value: 'toDo', label: 'Gepland' },
                        { value: 'all', label: 'Alles' },
                    ],
                },
            }),
            limit: new NumberControl({
                label: 'Aantal regels',
                description: 'Maximaal aantal regels in de tabel.',
                defaultValue: 8,
                options: { min: 1, max: 20, step: 1 },
            }),
        }
    }

    render(props) {
        return <Renderer {...props} />
    }
}

/**
 * @param {object} props
 * @param {{mode?: string, start: string|null, end: string|null}} [props.dateRange]
 * @param {'material'|'category'} [props.groupBy]
 * @param {'all'|'done'|'toDo'} [props.statusFilter]
 * @param {number} [props.limit]
 * @returns {JSX.Element}
 */
const Renderer = ({ dateRange, groupBy = 'material', statusFilter = 'done', limit = 8 }) => {
    const { housevisits } = useSettings()
    const isTemplate = isTemplateMode()
    const entries = useSharedCollection('AdressMaterials')
    // Niet via useSharedCollection: die laat zacht verwijderde documenten weg, en
    // een verwijderd materiaal staat nog bij bewoners geregistreerd. Zonder het
    // telt die maatregel als 'Onbekend materiaal' (inbox #284).
    const materials = useFirestoreQuery('Materials', { includeDeleted: true })
    const { range } = useWidgetPeriod(dateRange)

    const rows = useMemo(() => aggregateMeasuresByType({
        entries,
        materials,
        range,
        groupBy,
        statusFilter,
        limit,
    }), [entries, materials, range, groupBy, statusFilter, limit])

    if (isTemplate) return <ModulePlaceholder mode="template" />
    if (housevisits !== true) return <ModulePlaceholder mode="disabled" moduleLabel="Huishoudens" />

    return (
        <HouseVisitsReportTableVisualization
            rows={rows}
            labelHeader={groupBy === 'category' ? 'Categorie' : 'Maatregel'}
            countHeader="Aantal"
            emptyMessage="Geen maatregelen gevonden."
        />
    )
}
